import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScans, useScanStats } from '../../hooks/useScans.js';
import { EmptyState } from '../../components/common/EmptyState.jsx';
import { ScanResultBadge } from '../../components/dashboard/ScanResultBadge.jsx';
import { ScanStatsPanel } from '../../components/dashboard/ScanStatsPanel.jsx';
import { formatRelative, formatDateTime } from '../../lib/dates.js';
import { getVerifyState } from '../../lib/verify-states.js';

/**
 * Scan log viewer.
 *
 * Layout: 2-column desktop (main table + stats sidebar), stacked on mobile.
 * Features:
 *   - Filter by result (verified / under_review / no_longer_employed / unknown)
 *   - Cursor-based pagination via "Load more" button
 *   - Each row shows: time (relative + absolute on hover), staff, result, location
 *   - Click a staff row → jump to their staff record
 *
 * NOT included (deferred):
 *   - Filter by specific staff member (the endpoint supports it,
 *     but adding a staff picker here duplicates work that'll land
 *     with the "view this staff member's scan history" feature
 *     in the StaffEditor later).
 *   - CSV export (easy to add if tenants ask)
 *   - Time-range picker (30d is enough for now)
 */
export function ScansPage() {
  const [resultFilter, setResultFilter] = useState(null);

  const filters = resultFilter ? { result: resultFilter } : {};
  const { scans, hasMore, loading, loadingMore, error, loadMore, refresh } = useScans(filters);
  const statsResult = useScanStats();

  return (
    <>
      <div className="page-header">
        <div className="page-title-wrap">
          <h1 className="page-title">Scan activity</h1>
          <p className="page-subtitle">
            Every time someone scans a staff QR code, it lands here. Spot
            patterns, review unknowns, prove your vetting process to clients.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" role="alert">
          <strong>Couldn't load scan activity.</strong>{' '}
          <button type="button" onClick={refresh} className="btn btn-ghost" style={{ padding: 0, textDecoration: 'underline' }}>
            Try again
          </button>
        </div>
      )}

      <div className="scans-layout">
        <main>
          <div className="toolbar">
            <FilterButton label="All" active={resultFilter === null} onClick={() => setResultFilter(null)} />
            <FilterButton label="Verified" active={resultFilter === 'verified'} onClick={() => setResultFilter('verified')} />
            <FilterButton label="Under review" active={resultFilter === 'under_review'} onClick={() => setResultFilter('under_review')} />
            <FilterButton label="No longer employed" active={resultFilter === 'no_longer_employed'} onClick={() => setResultFilter('no_longer_employed')} />
            <FilterButton label="Unknown" active={resultFilter === 'unknown'} onClick={() => setResultFilter('unknown')} />
          </div>

          {loading && <ScansSkeleton />}

          {!loading && scans.length === 0 && (
            <EmptyState
              icon={
                <svg viewBox="0 0 64 64" width="32" height="32" aria-hidden="true">
                  <rect x="10" y="10" width="44" height="44" rx="6" />
                  <path d="M10 24h44M10 40h44M24 10v44M40 10v44" />
                </svg>
              }
              title={resultFilter ? 'No scans match that filter' : 'No scans yet'}
              body={
                resultFilter
                  ? 'Clear the filter to see all scan activity, or wait for more scans to come in.'
                  : 'Scans appear here as soon as someone verifies a staff member by scanning their QR code. Print a card, scan it to see this come alive.'
              }
              action={
                !resultFilter && (
                  <Link to="/dashboard/staff" className="btn btn-primary">
                    Back to Staff
                  </Link>
                )
              }
            />
          )}

          {!loading && scans.length > 0 && (
            <div className="scans-table-wrap">
              <table className="scans-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Staff</th>
                    <th>Result</th>
                    <th style={{ width: 100 }}>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map(scan => (
                    <ScanRow key={scan.id} scan={scan} />
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div className="scans-load-more">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <><span className="spinner" aria-hidden="true" /> Loading…</>
                    ) : (
                      'Load more'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </main>

        <ScanStatsPanel stats={statsResult.stats} loading={statsResult.loading} />
      </div>
    </>
  );
}

function FilterButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      className={`toolbar-filter ${active ? 'active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function ScanRow({ scan }) {
  const hasStaff = scan.staff_id && (scan.first_name || scan.last_name);
  const fullName = hasStaff
    ? `${scan.first_name || ''} ${scan.last_name || ''}`.trim()
    : null;
  const initials = hasStaff
    ? `${(scan.first_name || '').charAt(0)}${(scan.last_name || '').charAt(0)}`.toUpperCase()
    : '?';

  return (
    <tr>
      <td title={formatDateTime(scan.scanned_at)}>
        {formatRelative(scan.scanned_at)}
      </td>
      <td>
        {hasStaff ? (
          <Link
            to={`/dashboard/staff/${scan.staff_id}`}
            className="scan-staff-cell"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <span className="scan-staff-avatar">{initials}</span>
            <span className="scan-staff-meta">
              <span className="scan-staff-name">{fullName}</span>
              {scan.staff_role && <span className="scan-staff-role">{scan.staff_role}</span>}
            </span>
          </Link>
        ) : (
          <span className="muted" style={{ fontStyle: 'italic' }}>
            {scan.result === 'unknown' ? 'Unknown staff code' : 'Deleted staff record'}
          </span>
        )}
      </td>
      <td>
        <ScanResultBadge result={scan.result} />
      </td>
      <td className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
        {scan.ip_country || '—'}
      </td>
    </tr>
  );
}

function ScansSkeleton() {
  return (
    <div className="scans-table-wrap" aria-busy="true" aria-label="Loading scans">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="skeleton-card" style={{ height: 56, borderRadius: 0, borderBottom: '1px solid var(--line-soft)' }} />
      ))}
    </div>
  );
}
